import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { resolveAssetUrl } from "../../lib/api";
import { StatusBadge, Empty } from "../../components/Brand";
import { currency, fmtDate } from "../../lib/status";
import { toast } from "sonner";

const COMPLETED_STATUSES = [
    "job_completed",
    "customer_confirmed",
    "invoice_generated",
    "payment_completed",
];

export default function MyBookings() {
    const [bookings, setBookings] = useState(null);
    const [busyId, setBusyId] = useState(null);
    const nav = useNavigate();

    const load = async () => {
        const { data } = await api.get("/bookings");
        setBookings(data);
    };

    useEffect(() => {
        load();
    }, []);

    if (!bookings) return null;

    const rebook = async (e, b) => {
        e.preventDefault();
        e.stopPropagation();
        setBusyId(b.id);
        try {
            const { data } = await api.post(`/bookings/${b.id}/rebook`);
            toast.success(`Re-booked as ${data.booking_number}`);
            nav(`/customer/bookings/${data.id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not re-book");
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div>
            <div className="lx-label text-[#B2912A] mb-2">My Bookings</div>
            <h1 className="font-sans font-bold text-4xl sm:text-5xl text-[#0E0E0E] tracking-tight mb-8">
                All your <span className="lx-gold-text font-serif italic font-normal">services</span>,
                in one place.
            </h1>
            {bookings.length === 0 ? (
                <Empty
                    title="No bookings yet."
                    subtitle="Discover our curated catalogue and book your first service."
                    action={
                        <Link
                            to="/customer"
                            data-testid="empty-browse-link"
                            className="lx-btn-primary px-6 py-3 inline-block"
                        >
                            Browse services
                        </Link>
                    }
                />
            ) : (
                <div className="space-y-4">
                    {bookings.map((b) => {
                        const isCompleted = COMPLETED_STATUSES.includes(b.status);
                        return (
                            <div
                                key={b.id}
                                data-testid={`booking-row-${b.booking_number}`}
                                className="lx-card p-5 sm:p-6 group"
                            >
                                <div className="flex items-center gap-4 sm:gap-6">
                                    <Link
                                        to={`/customer/bookings/${b.id}`}
                                        className="flex items-center gap-4 sm:gap-6 flex-1 min-w-0"
                                    >
                                        {b.service?.image_url && (
                                            <img
                                                src={resolveAssetUrl(b.service.image_url)}
                                                alt=""
                                                className="w-20 h-20 sm:w-24 sm:h-24 object-cover hidden sm:block shrink-0"
                                            />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-3 mb-2 flex-wrap">
                                                <span className="lx-label text-[#B2912A]">
                                                    {b.booking_number}
                                                </span>
                                                <StatusBadge status={b.status} />
                                            </div>
                                            <h3 className="font-sans font-semibold text-lg sm:text-xl text-[#0E0E0E] truncate">
                                                {b.service?.name}
                                            </h3>
                                            <div className="text-sm text-[#555] mt-1">
                                                {fmtDate(b.scheduled_at)} ·{" "}
                                                {b.technician?.name || "Awaiting assignment"}
                                            </div>
                                        </div>
                                    </Link>
                                    <div className="text-right hidden sm:flex flex-col items-end gap-2 shrink-0">
                                        <div className="font-serif text-2xl text-[#180A21]">
                                            {currency(b.total_amount, b.currency)}
                                        </div>
                                        {isCompleted ? (
                                            <button
                                                data-testid={`rebook-${b.booking_number}`}
                                                onClick={(e) => rebook(e, b)}
                                                disabled={busyId === b.id}
                                                className="text-xs font-semibold tracking-[0.15em] uppercase px-3.5 py-1.5 bg-[#180A21] text-[#D4AF37] hover:bg-[#2B1438] transition disabled:opacity-50"
                                            >
                                                ↻ {busyId === b.id ? "Re-booking…" : "Re-book"}
                                            </button>
                                        ) : (
                                            <Link
                                                to={`/customer/bookings/${b.id}`}
                                                className="text-[#D4AF37] text-[10px] uppercase tracking-wider font-medium group-hover:underline"
                                            >
                                                Track →
                                            </Link>
                                        )}
                                    </div>
                                </div>
                                {/* Mobile rebook button */}
                                {isCompleted && (
                                    <button
                                        data-testid={`rebook-mob-${b.booking_number}`}
                                        onClick={(e) => rebook(e, b)}
                                        disabled={busyId === b.id}
                                        className="sm:hidden mt-3 w-full text-xs font-semibold tracking-[0.15em] uppercase py-2.5 bg-[#180A21] text-[#D4AF37] disabled:opacity-50"
                                    >
                                        ↻ {busyId === b.id ? "Re-booking…" : "Re-book this service"}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
